import { Link } from 'react-router-dom'
import { FilePlus, ListPlus, UserPlus, Video } from 'lucide-react'
import { PageHeader } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { usePermissions } from '@/hooks/usePermissions'
import { useDashboard, type DashboardStats } from '@/hooks/useDashboard'
import { StatsCards } from '@/components/dashboard/StatsCards'
import { ClientesDeudores } from '@/components/dashboard/ClientesDeudores'
import { IngresosMensuales } from '@/components/dashboard/IngresosMensuales'
import { TareasPendientesCard } from '@/components/dashboard/TareasPendientesCard'
import { ProximosVencimientosCard } from '@/components/dashboard/ProximosVencimientosCard'
import { MeetsCard } from '@/components/dashboard/MeetsCard'
import { NovedadesCard } from '@/components/dashboard/NovedadesCard'

const STATS_VACIAS: DashboardStats = {
  totalClientes: 0,
  clientesActivos: 0,
  totalDeuda: 0,
  totalCobrado: 0,
  saldoPendiente: 0,
  honorariosMensuales: 0,
}

// Both subcomponents share the same queryKey → TanStack Query dedupes to a single fetch
function FinancieroResumen() {
  const { data, isLoading } = useDashboard()
  if (!data && !isLoading) return null
  return <StatsCards stats={data?.stats ?? STATS_VACIAS} isLoading={isLoading} />
}

function FinancieroDetalle() {
  const { data, isLoading } = useDashboard()
  // El permiso, no la respuesta: durante la carga `data` es undefined y el título del gráfico
  // parpadeaba de "Ingresos" a "Ingresos y Gastos". Es el mismo flag con el que useDashboard
  // decide si pedir la serie, así que no se pueden desincronizar.
  const { puede } = usePermissions()
  if (!data && !isLoading) return null
  return (
    <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
      <ClientesDeudores clientes={data?.clientesDeudores ?? []} isLoading={isLoading} />
      <IngresosMensuales
        data={data?.resumenMensual ?? []}
        isLoading={isLoading}
        incluyeGastos={puede.verCuentas === true}
      />
    </div>
  )
}

export function Dashboard() {
  const { puede } = usePermissions()
  // Los bloques financieros muestran saldos, cobros y deudores, que salen de movimientos: sin
  // verMovimientos no se montan ni consultan (ORR-613). Los gastos siguen con verCuentas.
  // Tareas/vencimientos cards mirror the route guards of /tareas and /calendario.
  const verFinanciero = puede.verMovimientos === true
  const verTareas = puede.verTareas === true
  const verCalendario = puede.verCalendario === true
  const verClientes = puede.verClientes === true
  // ORR-217: la card se autogatea internamente por sub-sección, pero no la montamos
  // (ni disparamos sus queries) si ningún permiso de las 3 aplica.
  const verNovedades = verTareas || verCalendario || verClientes
  // Accesos al alta de cada módulo. Cada uno pide el permiso de crear y el que exige la ruta de destino.
  const accesos = [
    { etiqueta: 'Nuevo cliente', ruta: '/clientes?nuevo=true', Icono: UserPlus, visible: verClientes && puede.crearCliente === true },
    { etiqueta: 'Nueva tarea', ruta: '/tareas?nuevo=true', Icono: ListPlus, visible: verTareas && puede.crearTarea === true },
    { etiqueta: 'Nuevo Meet', ruta: '/calendario?nuevo=true', Icono: Video, visible: verCalendario && puede.crearEvento === true },
    { etiqueta: 'Nuevo presupuesto', ruta: '/presupuestos/nuevo', Icono: FilePlus, visible: puede.crearPresupuesto === true },
  ].filter((acceso) => acceso.visible)

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="Inicio" description="Resumen del estudio" />

      <main className="flex-1 p-4 md:p-6 space-y-4 md:space-y-6">
        {accesos.length > 0 && (
          <nav aria-label="Accesos rápidos" className="flex flex-wrap gap-2">
            {accesos.map(({ etiqueta, ruta, Icono }) => (
              <Button key={ruta} asChild variant="outline" size="sm">
                <Link to={ruta}>
                  <Icono className="h-4 w-4" />
                  {etiqueta}
                </Link>
              </Button>
            ))}
          </nav>
        )}

        {verFinanciero && <FinancieroResumen />}

        {/* Arriba del resto: es lo que puede estar por pasar en el día. La tarjeta se oculta
            sola cuando no hay reuniones próximas, y con ella su propio contenedor. */}
        {verCalendario && <MeetsCard />}

        {(verTareas || verCalendario) && (
          <div className="grid gap-4 md:gap-6 lg:grid-cols-2">
            {verTareas && <TareasPendientesCard />}
            {verCalendario && <ProximosVencimientosCard />}
          </div>
        )}

        {verNovedades && <NovedadesCard />}

        {verFinanciero && <FinancieroDetalle />}
      </main>
    </div>
  )
}
